// Shared helpers for linking figures back to their SEC source and ordering breakdown rows.

export function secFilingUrl(cik, accession) {
  if (!cik || !accession) return null;
  const cikNum = parseInt(String(cik).replace(/\D/g, ""), 10);
  const noDash = String(accession).replace(/-/g, "");
  return `https://www.sec.gov/Archives/edgar/data/${cikNum}/${noDash}/${accession}-index.htm`;
}

// De-camelCase fused XBRL-style member labels ("MajorCustomerOne" -> "Major Customer One",
// "TwoHoldingCompanies" -> "Two Holding Companies"). Only touches tokens that contain a generic
// concentration noun, so real brand names like "McKesson" or "iPhone" are left untouched.
const FUSED_GENERIC = /(Customers?|Members?|Compan(y|ies)|Distributors?|Clients?|Suppliers?|Holdings?|Resellers?|Wholesalers?|Parties|Group)/;
export function fmtLabel(s) {
  if (!s || typeof s !== "string") return s;
  return s.replace(/\b[A-Za-z]+\b/g, (w) => {
    if (!/[a-z][A-Z]/.test(w) || !FUSED_GENERIC.test(w)) return w;
    return w.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  });
}

// Build a scroll-to-text-fragment so the browser jumps to and highlights the disclosure.
// A text fragment matches CONTIGUOUS visible text, so a single phrase that spans commas
// (e.g. "Platform manufacturers, such as Sony, Microsoft, and Nintendo") will NOT match.
// We therefore use a textStart,textEnd RANGE: two short punctuation-free anchors (the start of
// the sentence and its end). The browser highlights everything between them, spanning punctuation.
function _runs(clean) {
  return clean
    .split(/[^A-Za-z'\- ]+/)               // break on digits/%/$/punctuation; KEEP hyphens intact
    .map((s) => s.replace(/(^[\s-]+|[\s-]+$)/g, "").split(" ").filter(Boolean))
    .filter((w) => w.length >= 2);          // need at least a 2-word punctuation-free anchor
}

export function textFragment(sentence) {
  if (!sentence) return "";
  const clean = sentence.replace(/^["']|["']$/g, "").replace(/\s+/g, " ").trim();
  const runs = _runs(clean);
  if (!runs.length) return "";
  // start anchor: first run with >=3 words (more unique), else the first run; cap 6 words.
  const startRun = runs.find((w) => w.length >= 3) || runs[0];
  const start = startRun.slice(0, 6).join(" ");
  // end anchor: last run with >=3 words, else the last run; last up to 6 words.
  const endCandidates = runs.filter((w) => w.length >= 3);
  const endRun = endCandidates.length ? endCandidates[endCandidates.length - 1] : runs[runs.length - 1];
  const end = endRun.slice(-6).join(" ");
  if (start.length < 6) return "";
  if (startRun === endRun || end === start) return `#:~:text=${encodeURIComponent(start)}`;
  return `#:~:text=${encodeURIComponent(start)},${encodeURIComponent(end)}`;
}

// Final deep link. XBRL dimensions use a SYNTHESIZED "Per XBRL…" sentence that does not exist
// verbatim in the filing, so a text fragment can never match — open the source directly (an R-file
// already points at the exact table). Only LLM prose (verbatim filing text) gets a scroll-to-text
// fragment so the browser jumps to and highlights the disclosure sentence.
export function sourceDeepLink(c, dim) {
  if (dim.source_url) {
    if (dim.source === "xbrl") return dim.source_url;
    return dim.source_url + textFragment(dim.raw_sentence);
  }
  return secFilingUrl(c.cik, dim.accession_number); // fallback: filing index
}

// Breakdown rows render by % descending — EXCEPT when every row is a sequential anonymized
// designation ("Customer One/Two/Three", "Customer A/B/C"), which reads wrong out of sequence;
// those sort by their designation instead.
const DESIG_RE = /^(?:customer|client|distributor|supplier|reseller|wholesaler|payor|payer)\s+([a-z]|one|two|three|four|five|six|seven|eight|nine|ten|\d+)$/i;
const DESIG_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
function desigKey(name) {
  const m = (name || "").trim().match(DESIG_RE);
  if (!m) return null;
  const d = m[1].toLowerCase();
  if (DESIG_WORDS[d]) return DESIG_WORDS[d];
  if (/^\d+$/.test(d)) return Number(d);
  return d.charCodeAt(0) - 96; // a=1, b=2, …
}
export function orderRows(rows) {
  if (!rows) return rows;
  if (rows.length > 1) {
    const keys = rows.map((r) => desigKey(r.name));
    if (keys.every((k) => k != null))
      return rows.map((r, i) => [keys[i], r]).sort((a, b) => a[0] - b[0]).map(([, r]) => r);
  }
  return [...rows].sort((a, b) =>
    (b.pct || 0) - (a.pct || 0) ||
    (a.name || "").localeCompare(b.name || "", undefined, { numeric: true }));
}
